// Login Convokko: pantalla dividida con panel de marca (navy) y formulario.
// Mantiene la lógica original de autenticación y de solicitud de cuenta (leads).
import { useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import SportsIcon from "@mui/icons-material/Sports";
import GroupsIcon from "@mui/icons-material/Groups";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

import logoConvokko from "assets/images/logo-convokko.png";
import { ModalShell, useNotify } from "components/Convokko";
import { API_BASE_URL } from "config";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "12px",
    backgroundColor: "#F8F9FB",
    "& fieldset": { borderColor: "#E6E9F0" },
    "&:hover fieldset": { borderColor: "#FBB53F" },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      "& fieldset": { borderColor: "#FBB53F", borderWidth: 2 },
    },
  },
};

const FEATURES = [
  {
    icon: <GroupsIcon />,
    title: "Equipos y plantillas",
    text: "Toda tu escuela organizada en un solo lugar",
  },
  {
    icon: <EventAvailableIcon />,
    title: "Convocatorias y asistencia",
    text: "Controla entrenamientos y partidos sin papeleos",
  },
  {
    icon: <EmojiEventsIcon />,
    title: "Clasificaciones al día",
    text: "Sigue la competición de todos tus equipos",
  },
];

export default function SignIn() {
  const notify = useNotify();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({
    nombre: "",
    email: "",
    telefono: "",
    escuela: "",
    mensaje: "",
  });
  const [leadErrors, setLeadErrors] = useState({});
  const [sendingLead, setSendingLead] = useState(false);

  const validateEmail = (value) => /\S+@\S+\.\S+/.test(value);

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    if (field === "email") {
      setEmail(value);
      setErrors((prev) => ({
        ...prev,
        email: value && !validateEmail(value) ? "Formato de email inválido" : "",
      }));
    } else {
      setPassword(value);
      setErrors((prev) => ({
        ...prev,
        password: value && value.length < 6 ? "Mínimo 6 caracteres" : "",
      }));
    }
  };

  const handleLeadInputChange = (field) => (e) => {
    const value = e.target.value;
    setLeadForm((prev) => ({ ...prev, [field]: value }));

    if (field === "email" && value && !validateEmail(value)) {
      setLeadErrors((prev) => ({ ...prev, email: "Email inválido" }));
    } else if (field === "telefono" && value && !/^\d{9}$/.test(value)) {
      setLeadErrors((prev) => ({ ...prev, telefono: "Debe tener 9 dígitos" }));
    } else {
      setLeadErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!email || !password) {
      notify.warning("Por favor, completa todos los campos");
      setLoading(false);
      return;
    }
    if (errors.email || errors.password) {
      notify.warning("Corrige los errores del formulario");
      setLoading(false);
      return;
    }

    const formData = new URLSearchParams();
    formData.append("username", email);
    formData.append("password", password);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
        credentials: "include",
      });

      const data = await res.json();

      if (!res.ok) {
        notify.error(data.detail || "Error al iniciar sesión");
        setLoading(false);
        return;
      }

      localStorage.clear();
      localStorage.setItem("token", data.access_token);
      notify.success("Login correcto");

      setTimeout(() => {
        window.location.replace("/dashboard");
      }, 800);
    } catch (error) {
      console.error("Error de conexión:", error);
      notify.error("Error al conectar con el servidor");
      setLoading(false);
    }
  };

  const handleCloseLeadModal = () => {
    setShowLeadModal(false);
    setLeadForm({ nombre: "", email: "", telefono: "", escuela: "", mensaje: "" });
    setLeadErrors({});
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!leadForm.nombre) newErrors.nombre = "Campo obligatorio";
    if (!leadForm.email) newErrors.email = "Campo obligatorio";
    else if (!validateEmail(leadForm.email)) newErrors.email = "Email inválido";
    if (!leadForm.telefono) newErrors.telefono = "Campo obligatorio";
    else if (!/^\d{9}$/.test(leadForm.telefono)) newErrors.telefono = "Debe tener 9 dígitos";
    if (!leadForm.escuela) newErrors.escuela = "Campo obligatorio";

    if (Object.keys(newErrors).length > 0) {
      setLeadErrors(newErrors);
      return;
    }

    setSendingLead(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/externas/solicitar-cuenta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: leadForm.nombre,
          email: leadForm.email,
          telefono: leadForm.telefono,
          escuela: leadForm.escuela,
          mensaje: leadForm.mensaje || null,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Error al enviar la solicitud");
      }

      notify.success(
        "¡Gracias por tu interés! Hemos recibido tu solicitud y te contactaremos pronto."
      );
      handleCloseLeadModal();
    } catch (error) {
      console.error("Error al enviar solicitud:", error);
      notify.error(error.message || "Error al enviar. Por favor, escríbenos a convokko@gmail.com");
    } finally {
      setSendingLead(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#F4F6FB" }}>
      {/* Panel de marca (solo escritorio) */}
      <Box
        sx={{
          flex: "1 1 55%",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          px: { md: 8, lg: 12 },
          py: 8,
          backgroundColor: "#1C2A55",
        }}
      >
        <Box
          component="img"
          src={logoConvokko}
          alt="Convokko"
          sx={{ width: 200, filter: "brightness(0) invert(1)", mb: 5 }}
        />

        <Typography variant="h3" sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2, mb: 1.5 }}>
          Digitaliza tu
          <Box component="span" sx={{ color: "#FBB53F" }}>
            {" "}
            escuela deportiva
          </Box>
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "rgba(255,255,255,0.75)", mb: 5, maxWidth: 440, position: "relative" }}
        >
          Equipos, convocatorias, asistencia y clasificaciones. Todo lo que tu escuela necesita, sin
          hojas de cálculo.
        </Typography>

        <Stack spacing={2.5} sx={{ position: "relative" }}>
          {FEATURES.map((f) => (
            <Stack key={f.title} direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "14px",
                  bgcolor: "rgba(251,181,63,0.16)",
                  color: "#FBB53F",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {f.icon}
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 700 }}>
                  {f.title}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  {f.text}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Panel del formulario */}
      <Box
        sx={{
          flex: "1 1 45%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2.5, sm: 6 },
          py: 6,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {/* Logo solo en móvil */}
          <Box textAlign="center" mb={4} display={{ xs: "block", md: "none" }}>
            <Box component="img" src={logoConvokko} alt="Convokko" sx={{ width: 180 }} />
          </Box>

          <Typography variant="h4" fontWeight={800} mb={0.5}>
            Bienvenido de nuevo
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={4}>
            Accede a tu cuenta de Convokko
          </Typography>

          <Box component="form" onSubmit={handleLogin} noValidate>
            <Stack spacing={2.5}>
              <TextField
                type="email"
                label="Correo electrónico"
                placeholder="ejemplo@correo.com"
                name="email"
                fullWidth
                value={email}
                onChange={handleInputChange("email")}
                error={!!errors.email}
                helperText={errors.email}
                disabled={loading}
                autoComplete="email"
                autoFocus
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "#FBB53F", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                type={showPassword ? "text" : "password"}
                label="Contraseña"
                placeholder="••••••••"
                name="password"
                fullWidth
                value={password}
                onChange={handleInputChange("password")}
                error={!!errors.password}
                helperText={errors.password}
                disabled={loading}
                autoComplete="current-password"
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "#FBB53F", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((p) => !p)}
                        edge="end"
                        disabled={loading}
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOff sx={{ fontSize: 20 }} />
                        ) : (
                          <Visibility sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                type="submit"
                disabled={loading || !email || !password || !!errors.email || !!errors.password}
                sx={{
                  height: 48,
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  borderRadius: "10px",
                  textTransform: "none",
                  color: "#fff",
                  backgroundColor: "#F59E0B",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#D97706", boxShadow: "none" },
                  "&:disabled": { backgroundColor: "#E4E7EE", color: "#9AA1B2", boxShadow: "none" },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1.5 }} />
                    Iniciando sesión...
                  </>
                ) : (
                  "Iniciar sesión"
                )}
              </Button>
            </Stack>
          </Box>

          <Stack direction="row" alignItems="center" spacing={2} my={3.5}>
            <Box flexGrow={1} height="1px" bgcolor="grey.300" />
            <Typography variant="caption" color="text.secondary">
              ¿Aún no tienes cuenta?
            </Typography>
            <Box flexGrow={1} height="1px" bgcolor="grey.300" />
          </Stack>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => setShowLeadModal(true)}
            startIcon={<SportsIcon />}
            sx={{
              height: 48,
              fontSize: "0.95rem",
              fontWeight: 700,
              borderRadius: "12px",
              textTransform: "none",
              borderWidth: 2,
              borderColor: "#FBB53F",
              color: "#D97706",
              "&:hover": {
                borderWidth: 2,
                borderColor: "#F59E0B",
                bgcolor: "rgba(251,181,63,0.06)",
              },
            }}
          >
            Solicitar una cuenta
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            textAlign="center"
            mt={3}
          >
            ¿Necesitas ayuda? Escríbenos a{" "}
            <Box
              component="a"
              href="mailto:convokko@gmail.com"
              sx={{ color: "#D97706", fontWeight: 600, textDecoration: "none" }}
            >
              convokko@gmail.com
            </Box>
          </Typography>

          <Typography variant="caption" display="block" textAlign="center" mt={1.5}>
            <Box
              component="a"
              href="/home"
              sx={{ color: "text.secondary", fontWeight: 600, textDecoration: "none" }}
            >
              Descubre todo lo que hace Convokko →
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Modal de solicitud de cuenta */}
      <ModalShell
        open={showLeadModal}
        onClose={handleCloseLeadModal}
        title="¡Únete a Convokko!"
        subtitle="Completa el formulario y te contactaremos pronto"
        icon={SportsIcon}
        actions={
          <>
            <Button onClick={handleCloseLeadModal} sx={{ color: "text.secondary" }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitLead}
              disabled={sendingLead}
              sx={{ borderRadius: 2 }}
            >
              {sendingLead ? "Enviando..." : "Enviar solicitud"}
            </Button>
          </>
        }
      >
        <Stack spacing={2.5} mt={0.5}>
          <TextField
            label="Nombre completo *"
            placeholder="Tu nombre"
            fullWidth
            value={leadForm.nombre}
            onChange={handleLeadInputChange("nombre")}
            error={!!leadErrors.nombre}
            helperText={leadErrors.nombre}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: "#FBB53F" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            type="email"
            label="Correo electrónico *"
            placeholder="tu@correo.com"
            fullWidth
            value={leadForm.email}
            onChange={handleLeadInputChange("email")}
            error={!!leadErrors.email}
            helperText={leadErrors.email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: "#FBB53F" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            type="tel"
            label="Teléfono *"
            placeholder="600123456"
            fullWidth
            value={leadForm.telefono}
            onChange={handleLeadInputChange("telefono")}
            error={!!leadErrors.telefono}
            helperText={leadErrors.telefono}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: "#FBB53F" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Nombre de tu escuela *"
            placeholder="Escuela deportiva"
            fullWidth
            value={leadForm.escuela}
            onChange={handleLeadInputChange("escuela")}
            error={!!leadErrors.escuela}
            helperText={leadErrors.escuela}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon sx={{ color: "#FBB53F" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Mensaje (opcional)"
            placeholder="Cuéntanos tus necesidades..."
            fullWidth
            multiline
            rows={3}
            value={leadForm.mensaje}
            onChange={handleLeadInputChange("mensaje")}
          />
        </Stack>
      </ModalShell>
    </Box>
  );
}
